// Phase 1A Phase 7C — useRolloverProtection hook.
//
// Read-only access to Phase-7B compounding-decision history for the
// authenticated user's Phantom chain. Two RLS-bounded SELECTs only:
//
//   1. chain_protected_amounts — running protection ledger. Latest row
//      (created_at desc) carries the cumulative_protected_amount; we
//      surface that single number as "Protected so far".
//   2. rollover_compounding_decisions — for the per-cycle decision
//      lookup used by the Chain History block. Returned as a
//      Map<cycleId, DecisionLite> for O(1) per-row enrichment.
//
// Trust contract:
//   - RLS on both tables is select_own only (Migration 0030, lines 91
//     and 134); INSERT/UPDATE/DELETE blocked at the policy level. The
//     hook never attempts writes.
//   - No RPC calls, no service-role client, no external HTTP, no
//     localStorage / sessionStorage. No randomness.
//   - cycleIds is filtered server-side via .in() so the network round
//     trip never returns rows the user doesn't own.
//   - When chainId is null (no Phantom chain yet) the hook returns the
//     empty truthful state — never invents data.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import type { Database } from "../db/types.ts";
import { getBrowserClient } from "../supabase/browser.ts";

type LedgerRow = Database["public"]["Tables"]["chain_protected_amounts"]["Row"];
type DecisionRow = Database["public"]["Tables"]["rollover_compounding_decisions"]["Row"];

// Slim shape exposed to UI rows — keeps the payload tight and avoids
// leaking internal columns the Vault doesn't render.
export interface DecisionLite {
  cycle_id: string;
  selected_option: "suggested" | "full" | "original" | "custom";
  confirmed_stake: number;
  confirmed_protected_amount: number;
  week_number: number;
  decision_at: string;
}

export interface RolloverProtectionState {
  cumulativeProtected: number | null; // null when no ledger rows yet
  decisionCount: number;
  latestDecision: DecisionLite | null;
  decisionsByCycleId: Map<string, DecisionLite>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const EMPTY_STATE: RolloverProtectionState = {
  cumulativeProtected: null,
  decisionCount: 0,
  latestDecision: null,
  decisionsByCycleId: new Map(),
  loading: false,
  error: null,
  refetch: async () => {},
};

function asNum(v: unknown): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") return Number(v);
  return Number.NaN;
}

function toLite(row: DecisionRow): DecisionLite {
  return {
    cycle_id: row.cycle_id,
    selected_option: row.selected_option as DecisionLite["selected_option"],
    confirmed_stake: asNum(row.confirmed_stake),
    confirmed_protected_amount: asNum(row.confirmed_protected_amount),
    week_number: row.week_number,
    decision_at: row.decision_at,
  };
}

export function useRolloverProtection(
  chainId: string | null,
  cycleIds: string[],
): RolloverProtectionState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [cumulativeProtected, setCumulative] = useState<number | null>(null);
  const [latestDecision, setLatest] = useState<DecisionLite | null>(null);
  const [decisionsByCycleId, setDecisionsByCycleId] = useState<Map<string, DecisionLite>>(
    new Map(),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stable cycle-id key so the effect doesn't re-fire on every render.
  const cycleKey = useMemo(() => [...cycleIds].sort().join(","), [cycleIds]);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user || chainId === null) {
      setCumulative(null);
      setLatest(null);
      setDecisionsByCycleId(new Map());
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    // Latest ledger row for this chain — its cumulative_protected_amount
    // is the running total per Migration 0030's RPC (line 412:
    // v_cumulative_total := v_cumulative_prior + v_confirmed_prot).
    const ledger = await supabase
      .from("chain_protected_amounts")
      .select("cumulative_protected_amount, created_at")
      .eq("chain_id", chainId)
      .order("created_at", { ascending: false })
      .limit(1);
    if (ledger.error) {
      setError(ledger.error.message);
      setLoading(false);
      return;
    }
    const latestLedger = (ledger.data?.[0] as LedgerRow | undefined) ?? null;
    setCumulative(latestLedger ? asNum(latestLedger.cumulative_protected_amount) : null);

    // All decisions for cycleIds we already have on the page. RLS does
    // the user-scoping; .in() keeps the wire payload tight.
    if (cycleIds.length === 0) {
      setLatest(null);
      setDecisionsByCycleId(new Map());
      setLoading(false);
      return;
    }
    const decisions = await supabase
      .from("rollover_compounding_decisions")
      .select(
        "cycle_id, selected_option, confirmed_stake, confirmed_protected_amount, week_number, decision_at",
      )
      .in("cycle_id", cycleIds)
      .order("decision_at", { ascending: false });
    if (decisions.error) {
      setError(decisions.error.message);
      setLoading(false);
      return;
    }
    const rows = (decisions.data ?? []) as DecisionRow[];
    const lites = rows.map(toLite);
    setLatest(lites[0] ?? null);
    const m = new Map<string, DecisionLite>();
    for (const d of lites) m.set(d.cycle_id, d);
    setDecisionsByCycleId(m);
    setLoading(false);
  }, [supabase, user, chainId, cycleIds]);

  useEffect(() => {
    void refetch();
    // cycleKey gates the effect; refetch closes over cycleIds for the
    // actual SELECT.
  }, [refetch, cycleKey]);

  return useMemo<RolloverProtectionState>(() => {
    if (chainId === null) return EMPTY_STATE;
    return {
      cumulativeProtected,
      decisionCount: decisionsByCycleId.size,
      latestDecision,
      decisionsByCycleId,
      loading,
      error,
      refetch,
    };
  }, [chainId, cumulativeProtected, latestDecision, decisionsByCycleId, loading, error, refetch]);
}
